import { defineStore } from "pinia";
import type {
  Order,
  User,
  PromotionCode,
  Device,
  LegacyDevice,
  PromotionCodeLogEntry,
} from "@/rvapi";
import RvApi from "@/rvapi";

const determineIdType = (q: string): string => {
  if (/^[0-9]{8,10}[0-9A-Z]{5}$/i.test(q)) {
    return "orderId";
  }
  if (/^CX[0-9]{5,}$/i.test(q)) {
    return "supportRef";
  }
  if (/^(dbg)?C[0-9]{5,}$/i.test(q)) {
    return "legacyCustomerNo";
  }
  if (
    /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\.zip$/i.test(
      q,
    )
  ) {
    return "batchid";
  }

  return "any";
};
export const useSearchStore = defineStore("search", {
  state: () => ({
    orderResult: [] as Order[],
    orderPromotionResult: [] as Order[],
    userResult: [] as User[],
    promotionResult: [] as PromotionCode[],
    promotionHistoryResult: [] as PromotionCodeLogEntry[],
    deviceResult: [] as Device[],
    legacyDeviceResult: [] as LegacyDevice[],
    batchIdResult: [] as Order[],
    errorMessage: "",
    searching: false,
    emptyResult: false,
  }),
  actions: {
    async search(q: string) {
      q = encodeURIComponent(q.trim());
      if (!q) {
        return;
      }

      this.errorMessage = "";
      this.emptyResult = false;
      this.searching = true;
      this.orderResult = [];
      this.userResult = [];
      this.promotionResult = [];
      this.deviceResult = [];
      this.legacyDeviceResult = [];
      this.batchIdResult = [];
      this.orderPromotionResult = [];
      this.promotionHistoryResult = [];

      const type = determineIdType(q);
      try {
        if (type === "orderId") {
          const order = await RvApi.searchOrderId(q);
          if (order) {
            this.orderResult = [order];
          }
        } else if (type === "supportRef") {
          this.deviceResult = await RvApi.searchSupportRef(q);
        } else if (type === "legacyCustomerNo") {
          this.legacyDeviceResult = await RvApi.searchLegacyCustomer(q);
        } else if (type === "batchid") {
          const id = q.substring(0, q.length - 4).toLowerCase();
          const result = await RvApi.searchBatchId(id);
          if (result) {
            this.batchIdResult = result.orders;
          }
        } else {
          const cloudPromise = RvApi.searchFreetext(q);
          const legacyPromise = RvApi.searchLegacyFreetext(q);
          const promotion = RvApi.searchPromotionCode(q);
          const promotionOrders = RvApi.searchGenericLookup<Order>(
            "history/promotionCode",
            q.toUpperCase(),
            10,
            0,
          );
          const promotionHistory =
            RvApi.searchGenericLookup<PromotionCodeLogEntry>(
              "promotioncodelog/code",
              q.toUpperCase(),
              10,
              0,
            );

          const cloud = await cloudPromise;
          this.userResult = cloud.users;
          this.orderResult = cloud.orders;
          const legacy = await legacyPromise;
          this.legacyDeviceResult = legacy.legacy;
          this.deviceResult = legacy.devices;
          this.orderPromotionResult = await promotionOrders;
          this.promotionResult = await promotion;
          this.promotionHistoryResult = await promotionHistory;
        }

        if (
          this.orderResult.length == 0 &&
          this.userResult.length == 0 &&
          this.promotionResult.length == 0 &&
          this.deviceResult.length == 0 &&
          this.legacyDeviceResult.length == 0 &&
          this.batchIdResult.length == 0 &&
          this.orderPromotionResult.length == 0 &&
          this.promotionHistoryResult.length == 0
        ) {
          this.emptyResult = true;
        }
      } catch (e) {
        this.errorMessage = "Timed out, try again.";
      }
      this.searching = false;
    },
  },
});
